import type { FormGroup } from '@angular/forms';
import type {
    Form,
    ReactiveDropdownCatalogPhase,
    ReactiveFormSupportDataMetricsEvent,
    ReactiveFormSupportDataMetricsSink,
} from '../../interfaces/form.interface';
import {
    ReactiveFormLoadOrchestrator,
    type ReactiveFormLoadPlan,
    type ReactiveFormLoadPolicy,
    type ReactiveFormLoadReadinessSnapshot,
} from './reactive-form-load-orchestrator';
import { ReactiveFormSupportDataExecutor } from './reactive-form-support-data-executor';

export type ReactiveFormLoadSessionPhase = ReactiveDropdownCatalogPhase;

export type ReactiveFormLoadSessionEventType =
    'planCreated' |
    'phaseLoading' |
    'phaseReady' |
    'phaseFailed' |
    'renderReady' |
    'dropdownMappingsRefreshed' |
    'readinessChanged' |
    'supportDataMetrics';

export interface ReactiveFormLoadSessionEvent {
    readonly type: ReactiveFormLoadSessionEventType;
    readonly phase?: ReactiveFormLoadSessionPhase | undefined;
    readonly readiness?: ReactiveFormLoadReadinessSnapshot | undefined;
    readonly supportDataMetrics?: ReactiveFormSupportDataMetricsEvent | undefined;
    readonly error?: unknown;
}

export interface ReactiveFormLoadPhaseState {
    readonly loading: boolean;
    readonly ready: boolean;
    readonly failed: boolean;
    readonly error: unknown;
}

export interface ReactiveFormLoadStateSnapshot {
    readonly plan: ReactiveFormLoadPlan | null;
    readonly renderReady: boolean;
    readonly dropdownMappingsReady: boolean;
    readonly blockingErrors: boolean;
    readonly phases: Record<ReactiveFormLoadSessionPhase, ReactiveFormLoadPhaseState>;
    readonly readiness: ReactiveFormLoadReadinessSnapshot;
    readonly events: readonly ReactiveFormLoadSessionEvent[];
}

export interface ReactiveFormLoadPhaseContext<TModel = unknown> {
    readonly phase: ReactiveFormLoadSessionPhase;
    readonly form: Form<TModel>;
    readonly plan: ReactiveFormLoadPlan;
    readonly model: FormGroup | null;
    readonly previousData: TModel | null;
    readonly getValue: (fieldName: string) => unknown;
}

export type ReactiveFormLoadPhaseCallback<TModel = unknown> = (
    context: ReactiveFormLoadPhaseContext<TModel>,
) => Promise<boolean | void> | boolean | void;

export type ReactiveFormLoadRenderCallback<TModel = unknown> = (
    context: ReactiveFormLoadPhaseContext<TModel>,
) => Promise<void> | void;

export interface ReactiveFormLoadRunnerOptions<TModel = unknown> {
    readonly form: Form<TModel>;
    readonly policy?: ReactiveFormLoadPolicy | undefined;
    readonly model?: FormGroup | null | undefined;
    readonly previousData?: TModel | null | undefined;
    readonly getValue?: ((fieldName: string) => unknown) | undefined;
    readonly loadDropdownPhase?: ReactiveFormLoadPhaseCallback<TModel> | undefined;
    readonly loadSupportDataPhase?: ReactiveFormLoadPhaseCallback<TModel> | undefined;
    readonly render?: ReactiveFormLoadRenderCallback<TModel> | undefined;
    readonly refreshDropdownMappings?: ReactiveFormLoadRenderCallback<TModel> | undefined;
    readonly onSupportDataMetrics?: ReactiveFormSupportDataMetricsSink | undefined;
    readonly onEvent?: ((event: ReactiveFormLoadSessionEvent) => void) | undefined;
}

const EMPTY_PHASE_STATE: ReactiveFormLoadPhaseState = {
    loading: false,
    ready: false,
    failed: false,
    error: null,
};

const LOAD_PHASES: readonly ReactiveFormLoadSessionPhase[] = ['critical', 'deferred', 'lazy'];

/** Stores load-session progress so hosts can expose one uniform readiness contract. */
export class ReactiveFormLoadStateStore {
    private planValue: ReactiveFormLoadPlan | null = null;
    private renderReadyValue = false;
    private dropdownMappingsReadyValue = false;
    private blockingErrorsValue = false;
    private readonly phasesValue = new Map<ReactiveFormLoadSessionPhase, ReactiveFormLoadPhaseState>();
    private eventsValue: ReactiveFormLoadSessionEvent[] = [];

    public constructor(
        private readonly policy: ReactiveFormLoadPolicy = {},
        private readonly onEvent?: ((event: ReactiveFormLoadSessionEvent) => void) | undefined,
    ) {
        LOAD_PHASES.forEach((phase) => this.phasesValue.set(phase, EMPTY_PHASE_STATE));
    }

    public setPlan(plan: ReactiveFormLoadPlan): void {
        this.planValue = plan;
        this.renderReadyValue = false;
        this.dropdownMappingsReadyValue = false;
        this.blockingErrorsValue = false;
        this.resetPhases();
        this.track({ type: 'planCreated' });
        this.trackReadiness();
    }

    public markPhaseLoading(phase: ReactiveFormLoadSessionPhase): void {
        this.setPhaseState(phase, {
            loading: true,
            ready: false,
            failed: false,
            error: null,
        });
        this.track({ type: 'phaseLoading', phase });
        this.trackReadiness();
    }

    public markPhaseReady(phase: ReactiveFormLoadSessionPhase): void {
        this.setPhaseState(phase, {
            loading: false,
            ready: true,
            failed: false,
            error: null,
        });
        this.track({ type: 'phaseReady', phase });
        this.trackReadiness();
    }

    public markPhaseFailed(phase: ReactiveFormLoadSessionPhase, error: unknown = null): void {
        this.setPhaseState(phase, {
            loading: false,
            ready: false,
            failed: true,
            error,
        });
        this.blockingErrorsValue = phase === 'critical' || this.blockingErrorsValue;
        this.track({ type: 'phaseFailed', phase, error });
        this.trackReadiness();
    }

    public markRenderReady(): void {
        this.renderReadyValue = true;
        this.track({ type: 'renderReady' });
        this.trackReadiness();
    }

    public markDropdownMappingsRefreshed(): void {
        this.dropdownMappingsReadyValue = true;
        this.track({ type: 'dropdownMappingsRefreshed' });
        this.trackReadiness();
    }

    public trackSupportDataMetrics(metrics: ReactiveFormSupportDataMetricsEvent): void {
        this.track({
            type: 'supportDataMetrics',
            supportDataMetrics: metrics,
        });
    }

    public setBlockingErrors(hasBlockingErrors: boolean): void {
        this.blockingErrorsValue = hasBlockingErrors;
        this.trackReadiness();
    }

    public snapshot(): ReactiveFormLoadStateSnapshot {
        return {
            plan: this.planValue,
            renderReady: this.renderReadyValue,
            dropdownMappingsReady: this.dropdownMappingsReadyValue,
            blockingErrors: this.blockingErrorsValue,
            phases: {
                critical: this.getPhaseState('critical'),
                deferred: this.getPhaseState('deferred'),
                lazy: this.getPhaseState('lazy'),
            },
            readiness: this.snapshotReadiness(),
            events: [...this.eventsValue],
        };
    }

    public snapshotReadiness(): ReactiveFormLoadReadinessSnapshot {
        const criticalState = this.getPhaseState('critical');
        const deferredState = this.getPhaseState('deferred');
        const lazyState = this.getPhaseState('lazy');

        return ReactiveFormLoadOrchestrator.snapshotReadiness({
            shellReady: this.planValue !== null,
            recordReady: this.planValue !== null,
            criticalCatalogsReady: criticalState.ready,
            criticalSupportDataReady: criticalState.ready,
            hydrationReady: this.renderReadyValue,
            deferredCatalogsReady: !deferredState.failed,
            deferredSupportDataReady: !deferredState.failed,
            lazyCatalogsReady: lazyState.ready,
            minTimeExhausted: true,
            hasBlockingErrors: this.blockingErrorsValue,
        }, this.policy);
    }

    private resetPhases(): void {
        LOAD_PHASES.forEach((phase) => this.phasesValue.set(phase, EMPTY_PHASE_STATE));
    }

    private setPhaseState(phase: ReactiveFormLoadSessionPhase, state: ReactiveFormLoadPhaseState): void {
        this.phasesValue.set(phase, state);
    }

    private getPhaseState(phase: ReactiveFormLoadSessionPhase): ReactiveFormLoadPhaseState {
        return this.phasesValue.get(phase) ?? EMPTY_PHASE_STATE;
    }

    private trackReadiness(): void {
        this.track({
            type: 'readinessChanged',
            readiness: this.snapshotReadiness(),
        });
    }

    private track(event: ReactiveFormLoadSessionEvent): void {
        this.eventsValue = [...this.eventsValue, event];
        this.onEvent?.(event);
    }
}

/** Runs the deterministic plan phases without leaking pool/build details to host components. */
export class ReactiveFormLoadRunner<TModel = unknown> {
    public readonly state: ReactiveFormLoadStateStore;

    private planValue: ReactiveFormLoadPlan | null = null;

    public constructor(private readonly options: ReactiveFormLoadRunnerOptions<TModel>) {
        this.state = new ReactiveFormLoadStateStore(options.policy, options.onEvent);
    }

    public get plan(): ReactiveFormLoadPlan | null {
        return this.planValue;
    }

    public createPlan(): ReactiveFormLoadPlan {
        const plan = ReactiveFormLoadOrchestrator.createPlan(this.options.form, {
            ...(this.options.previousData === undefined ? {} : { previousData: this.options.previousData }),
            ...(this.options.policy === undefined ? {} : { policy: this.options.policy }),
        });

        this.planValue = plan;
        this.state.setPlan(plan);

        return plan;
    }

    public async runCritical(): Promise<boolean> {
        return this.runPhase('critical');
    }

    public async runDeferred(): Promise<boolean> {
        return this.runPhase('deferred');
    }

    public async runLazy(): Promise<boolean> {
        return this.runPhase('lazy');
    }

    public async render(): Promise<void> {
        const context = this.createContext('critical');

        await this.options.render?.(context);
        this.state.markRenderReady();
    }

    public async refreshDropdownMappings(): Promise<void> {
        const context = this.createContext('critical');

        await this.options.refreshDropdownMappings?.(context);
        this.state.markDropdownMappingsRefreshed();
    }

    public async runPhase(phase: ReactiveFormLoadSessionPhase): Promise<boolean> {
        const context = this.createContext(phase);

        this.state.markPhaseLoading(phase);

        try {
            const dropdownsReady = await ReactiveFormLoadRunner.resolveCallback(this.options.loadDropdownPhase, context);
            const supportDataReady = await this.loadSupportDataPhase(context);
            const ready = dropdownsReady && supportDataReady;

            if (ready) {
                this.state.markPhaseReady(phase);
            } else {
                this.state.markPhaseFailed(phase);
            }

            return ready;
        } catch (error: unknown) {
            this.state.markPhaseFailed(phase, error);

            return false;
        }
    }

    private async loadSupportDataPhase(context: ReactiveFormLoadPhaseContext<TModel>): Promise<boolean> {
        if (this.options.loadSupportDataPhase) {
            return ReactiveFormLoadRunner.resolveCallback(this.options.loadSupportDataPhase, context);
        }

        return ReactiveFormSupportDataExecutor.load({
            form: context.form,
            loadPlan: context.plan,
            phase: context.phase,
            model: context.model,
            previousData: context.previousData,
            getValue: context.getValue,
            onMetrics: (metrics) => this.handleSupportDataMetrics(metrics),
            toggleLoading: false,
        });
    }

    private handleSupportDataMetrics(metrics: ReactiveFormSupportDataMetricsEvent): void {
        this.state.trackSupportDataMetrics(metrics);
        this.options.onSupportDataMetrics?.(metrics);
    }

    private createContext(phase: ReactiveFormLoadSessionPhase): ReactiveFormLoadPhaseContext<TModel> {
        const plan = this.planValue ?? this.createPlan();

        return {
            phase,
            form: this.options.form,
            plan,
            model: this.options.model ?? null,
            previousData: this.options.previousData ?? null,
            getValue: this.options.getValue ?? ReactiveFormLoadRunner.emptyValueGetter,
        };
    }

    private static async resolveCallback<TModel>(
        callback: ReactiveFormLoadPhaseCallback<TModel> | undefined,
        context: ReactiveFormLoadPhaseContext<TModel>,
    ): Promise<boolean> {
        if (!callback) {
            return true;
        }

        const result = await callback(context);

        return result !== false;
    }

    private static emptyValueGetter(): unknown {
        return undefined;
    }
}

/** High-level session: critical first, render when usable, deferred in the same flow, lazy on demand. */
export class ReactiveFormLoadSession<TModel = unknown> {
    public readonly runner: ReactiveFormLoadRunner<TModel>;

    public constructor(options: ReactiveFormLoadRunnerOptions<TModel>) {
        this.runner = new ReactiveFormLoadRunner(options);
    }

    public get state(): ReactiveFormLoadStateStore {
        return this.runner.state;
    }

    public get plan(): ReactiveFormLoadPlan | null {
        return this.runner.plan;
    }

    public start(): Promise<ReactiveFormLoadReadinessSnapshot> {
        return this.runInitialLoad();
    }

    public async loadLazy(): Promise<ReactiveFormLoadReadinessSnapshot> {
        await this.runner.runLazy();

        return this.state.snapshotReadiness();
    }

    private async runInitialLoad(): Promise<ReactiveFormLoadReadinessSnapshot> {
        this.runner.createPlan();

        const criticalReady = await this.runner.runCritical();

        if (!criticalReady) {
            return this.state.snapshotReadiness();
        }

        await this.runner.render();
        await this.runner.refreshDropdownMappings();
        await this.runner.runDeferred();

        return this.state.snapshotReadiness();
    }
}
